import React, { useEffect, useRef, useState } from 'react';
import { api, mediaUrl } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Award, Star, GraduationCap, Gift, Sparkles, Flame, Trophy, Camera, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Profile() {
    const { refresh } = useAuth();
    const [data, setData] = useState(null);
    const [ledger, setLedger] = useState([]);
    const [rewards, setRewards] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const [msg, setMsg] = useState('');
    const fileRef = useRef(null);
    const [avatarBusy, setAvatarBusy] = useState(false);

    const onAvatarPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Please choose an image'); return; }
        if (file.size > 8 * 1024 * 1024) { toast.error('Image must be under 8 MB'); return; }
        setAvatarBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await api.post('/trainee/profile/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Profile picture updated');
            await reload();
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Upload failed');
        } finally {
            setAvatarBusy(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const reload = async () => {
        try {
            const d = await api.get('/trainee/dashboard');
            setData(d.data);
            const l = await api.get('/trainee/points-ledger'); setLedger(l.data);
            const r = await api.get('/rewards'); setRewards(r.data);
        } catch { /* keep last good state */ }
    };
    useEffect(() => { reload(); }, []);

    const redeem = async (id) => {
        setBusyId(id); setMsg('');
        try {
            await api.post(`/trainee/rewards/${id}/redeem`);
            setMsg('Reward redeemed! Check your email for the voucher (mock).');
            reload();
        } catch (e) { setMsg(e?.response?.data?.detail || 'Could not redeem'); }
        finally { setBusyId(null); }
    };

    if (!data) return null;
    const u = data.user, s = data.stats;

    return (
        <div data-testid="profile-page" className="-mx-4 sm:-mx-6 lg:mx-0">
            {/* Layered navy header */}
            <div className="bg-brand-navy pt-10 pb-24 px-6 rounded-b-[40px] lg:rounded-[28px] text-center relative overflow-hidden" data-testid="profile-header">
                <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 80% 0%, rgba(255,212,0,0.6), transparent 55%)' }} />
                <div className="relative">
                    <div className="w-24 h-24 rounded-full border-4 border-brand-yellow/20 p-1 mx-auto relative">
                        <div className="w-full h-full rounded-full border-2 border-brand-yellow bg-white text-brand-navy flex items-center justify-center font-black text-3xl overflow-hidden">
                            {u.avatar_url
                                ? <img src={mediaUrl(u.avatar_url)} alt={u.full_name} className="w-full h-full object-cover" />
                                : (u.full_name?.[0])}
                        </div>
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={avatarBusy}
                            data-testid="profile-avatar-upload-btn"
                            aria-label="Change profile picture"
                            className="absolute -bottom-1 -right-1 w-8 h-8 grid place-items-center rounded-full bg-brand-yellow text-brand-black shadow-md ring-2 ring-brand-navy hover:brightness-105 transition disabled:opacity-60">
                            {avatarBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                        </button>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatarPick} data-testid="profile-avatar-input" />
                        <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-brand-yellow text-brand-black text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">
                            {u.role?.replaceAll('_', ' ')}
                        </span>
                    </div>
                    <h1 className="text-white text-xl font-bold mt-5" data-testid="profile-name">{u.full_name}</h1>
                    <div className="text-brand-yellow/70 text-xs font-medium mt-0.5">{u.department || '—'} · {u.region || '—'}</div>
                </div>
            </div>

            {/* Overlapping stats card */}
            <div className="px-4 sm:px-6 lg:px-0">
                <div className="-mt-14 relative z-10 bg-white rounded-2xl shadow-[0_8px_32px_rgba(35,31,32,0.12)] p-4 grid grid-cols-4 gap-1 border border-brand-navy/5" data-testid="profile-stats">
                    <StatItem icon={Star} value={s.points} label="Points" />
                    <StatItem icon={Flame} value={s.streak_days} label="Streak" />
                    <StatItem icon={Trophy} value={`#${s.rank}`} label="Rank" />
                    <StatItem icon={GraduationCap} value={s.certificates_count} label="Certs" />
                </div>
            </div>

            <div className="px-4 sm:px-6 lg:px-0 mt-6 grid lg:grid-cols-3 gap-5">
                <Card icon={GraduationCap} iconCls="text-emerald-600" title="Certificates">
                    {data.certificates.length ? data.certificates.map((c) => (
                        <div key={c.id} className="p-3 rounded-xl bg-slate-50 border border-brand-navy/5" data-testid={`profile-cert-${c.certificate_id}`}>
                            <div className="text-[11px] text-brand-navy/50">ID: {c.certificate_id}</div>
                            <div className="font-bold text-brand-black text-sm">{c.course_title}</div>
                            <div className="text-xs text-brand-navy/55 mt-0.5">Score: {Math.round(c.score)}%</div>
                        </div>
                    )) : <Empty>Pass a course&apos;s final quiz to earn a certificate.</Empty>}
                </Card>

                <Card icon={Award} iconCls="text-brand-yellow" title="Badges">
                    <div className="flex flex-wrap gap-2">
                        {data.badges.length ? data.badges.map((b) => (
                            <motion.div key={b.code} initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: 'spring', stiffness: 200, damping: 14 }}
                                className="px-3 py-2 rounded-xl bg-brand-yellow/10 border border-brand-yellow/30 text-brand-black text-xs font-bold flex items-center gap-2"
                                data-testid={`profile-badge-${b.code}`}>
                                <Award className="h-3.5 w-3.5 text-brand-yellow" /> {b.name}
                            </motion.div>
                        )) : <Empty>Complete courses and play games to earn badges.</Empty>}
                    </div>
                </Card>

                <Card icon={Sparkles} iconCls="text-brand-navy" title="Points Activity">
                    <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
                        {ledger.length ? ledger.slice(0, 20).map((l) => (
                            <div key={l.id} className="flex items-center justify-between text-xs" data-testid={`ledger-${l.id}`}>
                                <div className="text-brand-navy/70 truncate flex-1 font-medium">{l.reason}</div>
                                <div className={l.points > 0 ? 'text-emerald-600 font-bold' : 'text-rose-600 font-bold'}>{l.points > 0 ? `+${l.points}` : l.points}</div>
                            </div>
                        )) : <Empty>No activity yet.</Empty>}
                    </div>
                </Card>
            </div>

            <div className="px-4 sm:px-6 lg:px-0 mt-5 mb-2">
                <div className="bg-white rounded-2xl border border-brand-navy/5 p-5 shadow-[0_2px_12px_rgba(35,31,32,0.05)]">
                    <h2 className="font-bold text-lg tracking-tight text-brand-black flex items-center gap-2 mb-3"><Gift className="h-5 w-5 text-brand-yellow" /> Redeem Rewards</h2>
                    {msg && <div className="mb-3 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{msg}</div>}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {rewards.map((r) => (
                            <div key={r.id} className="rounded-xl border border-brand-navy/5 bg-slate-50/50 p-3 flex gap-3" data-testid={`redeem-${r.id}`}>
                                <div className="h-16 w-16 rounded-lg bg-brand-navy/5 overflow-hidden flex-shrink-0">
                                    {r.image_url && <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-brand-black text-sm leading-tight">{r.name}</div>
                                    <div className="text-[11px] text-brand-navy/55 line-clamp-1">{r.description}</div>
                                    <div className="flex items-center justify-between mt-1.5">
                                        <div className="text-xs font-black text-brand-black">{r.cost_points} pts</div>
                                        <button
                                            disabled={busyId === r.id || s.points < r.cost_points}
                                            onClick={() => redeem(r.id)}
                                            className="text-xs px-3 py-1.5 rounded-lg bg-brand-navy text-brand-yellow font-bold hover:bg-brand-black disabled:opacity-40 transition"
                                            data-testid={`redeem-btn-${r.id}`}>
                                            {busyId === r.id ? '…' : 'Redeem'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatItem({ icon: Icon, value, label }) {
    return (
        <div className="flex flex-col items-center">
            <Icon className="h-4 w-4 text-brand-yellow mb-1" />
            <div className="text-lg font-black text-brand-black leading-none">{value}</div>
            <div className="text-[10px] font-bold text-brand-navy/55 uppercase tracking-wide mt-1">{label}</div>
        </div>
    );
}

function Card({ icon: Icon, iconCls, title, children }) {
    return (
        <div className="bg-white rounded-2xl border border-brand-navy/5 p-5 shadow-[0_2px_12px_rgba(35,31,32,0.05)]">
            <h2 className="font-bold text-lg tracking-tight text-brand-black flex items-center gap-2"><Icon className={`h-5 w-5 ${iconCls}`} /> {title}</h2>
            <div className="mt-3 space-y-2">{children}</div>
        </div>
    );
}

function Empty({ children }) {
    return <div className="text-xs text-brand-navy/45 font-medium">{children}</div>;
}
